<template>
    <div
        ref="root"
        class="hts-data-table h-full">
        <div
            v-show="!loaded"
            class="h-full bg-theme-100"
            style="height: 500px; text-align: center; font-size: 18px">
            Initializing implementing
        </div>
        <div
            v-show="loaded"
            class="h-full">
            <div
                v-if="useSelection && isSelectAllTable"
                class="hts-data-table-actions">
                <div class="py-1">
                    <span class="h-6 block" />
                </div>
                <div
                    v-if="hasSelectedItem && !showDeleted"
                    class="flex items-center space-x-4">
                    <div
                        v-if="(!showArchived && !isDeleted && useArchive && useArchive.permissions) || selectionActions.length"
                        ref="actionTable"
                        class="dropdown dropdown-bottom select-none">
                        <span tabindex="0">
                            <div class="hts-data-table-actions-button">
                                <span>
                                    {{ $t('labels.action') }}
                                </span>
                                <hi-chevron-down-icon
                                    class="h-6 w-6 text-main-500"
                                    aria-hidden="true" />
                            </div>
                        </span>
                        <ul
                            tabindex="0"
                            class="dropdown-content menu shadow bg-base-100 rounded p-1 w-52 z-10">
                            <li
                                v-if="!showArchived && !isDeleted && useArchive && useArchive.permissions"
                                @click="openArchive()">
                                <div class="flex py-0 text-error-600">
                                    <span class="p-0">
                                        {{ $t('labels.archive') }}
                                    </span>
                                </div>
                            </li>

                            <template v-if="selectionActions.length">
                                <div
                                    v-for="(item, index) in selectionActions"
                                    :key="index">
                                    <li
                                        v-if="checkActionVisible(item)"
                                        @click="handleClickSelectionAction(item)">
                                        <div
                                            class="flex py-0"
                                            :class="item.class">
                                            <component
                                                :is="item.icon"
                                                v-if="item.icon"
                                                class="icon w-5 h-5" />
                                            <span class="p-0">
                                                {{ item.title || '...' }}
                                            </span>
                                        </div>
                                    </li>
                                </div>
                            </template>
                            <template v-if="showArchived && useDelete">
                                <li
                                    class="text-error-500"
                                    @click="handleDeleteSelectedItems">
                                    <div>
                                        <hi-archive-box-x-mark-icon class="w-5 h-5 inline-block align-middle" />
                                        <span class="inline-block align-middle">
                                            {{ $t('labels.delete') }}
                                        </span>
                                    </div>
                                </li>
                            </template>
                        </ul>
                    </div>
                    <div class="flex items-center select-none">
                        <span>
                            {{ $t('labels.selected')
                            }}<span class="mx-1">
                                {{ totalSelectedItem }}
                            </span>{{ $t('labels.item') }}
                        </span>
                        <hi-minus-circle-icon
                            class="h-6 w-6 text-error-500 hover:text-error-600 cursor-pointer ml-1"
                            aria-hidden="true"
                            @click="clearAllSelection" />
                    </div>
                </div>
                <hts-modal
                    :show="archiveVisible"
                    :title="$t('labels.archive')"
                    type="confirm"
                    :content="$t('labels.confirmArchiveMessage')"
                    @close="hideArchive"
                    @confirm="confirmArchive" />
                <hts-modal
                    v-model="restoreVisible"
                    :title="$t('labels.restore')"
                    type="confirm"
                    :content="$t('labels.confirmRestoreMessage')"
                    @close="hideRestore"
                    @confirm="confirmRestore" />
            </div>
            <template v-if="initialized">
                <ag-grid-vue
                    :row-model-type="isServerSide ? 'serverSide' : undefined"
                    :server-side-sort-on-server="serverSideSortOnServer"
                    :server-side-filter-on-server="serverSideFilterOnServer"
                    :column-defs="cHeaders"
                    :default-col-def="options"
                    :icons="icons"
                    :row-data="items"
                    class="ag-theme-alpine"
                    :style="cStyle"
                    :animate-rows="animateRows"
                    :server-side-infinite-scroll="!!isServerSide"
                    :pagination="pagination"
                    :pagination-page-size="cPaginationPageSize"
                    :cache-block-size="cPaginationPageSize"
                    :row-selection="rowSelection"
                    :get-context-menu-items="getContextMenuItems"
                    :side-bar="sideBar"
                    :master-detail="masterDetail"
                    :detail-row-auto-height="detailRowAutoHeight"
                    :detail-cell-renderer-params="detailCellRendererParams"
                    :detail-cell-renderer="detailCellRenderer"
                    :detail-row-height="detailRowHeight"
                    :auto-group-column-def="autoGroupColumnDef"
                    :is-row-master="isRowMaster"
                    :always-show-horizontal-scroll="alwaysShowHorizontalScroll"
                    :always-show-vertical-scroll="alwaysShowVerticalScroll"
                    :suppress-pagination-panel="true"
                    :suppress-row-transform="isSuppressRowTransform"
                    :single-click-edit="singleClickEdit"
                    :suppress-row-click-selection="suppressRowClickSelection"
                    :locale-text="localeText"
                    :get-row-style="getRowStyle"
                    :is-row-selectable="isRowSelectable"
                    :overlay-no-rows-template="$t('labels.notData')"
                    :overlay-loading-template="$t('labels.loadingData')"
                    :get-row-id="getRowId"
                    :enable-cell-change-flash="enableCellChangeFlash"
                    :framework-components="components"
                    @first-data-rendered="onFirstDataRendered"
                    @grid-ready="onGridReady"
                    @drag-stopped="onDragStopped"
                    @sort-changed="onSortChanged"
                    @selection-changed="handleChangeSelection"
                    @pagination-changed="onPaginationChanged"
                    @cell-clicked="onRowClicked"
                    @filterChanged="onFilterChanged" />
                <div
                    v-if="isFooter"
                    class="hts-data-table-footer">
                    <div class="mx-2 flex gap-2 flex-wrap">
                        <switch-button
                            v-if="selectAllVisible"
                            v-model="toggleSelectedAll"
                            :label="$t('labels.choseAll') + ' ' + totalItem + ' ' + $t('labels.items')"
                            icon="hi-check-circle-icon"
                            @change="onSelectionAll" />
                        <switch-button
                            v-if="showDeletedEnable"
                            v-model="showDeleted"
                            :label="$t('labels.deleted')"
                            @change="refresh()" />
                        <switch-button
                            v-if="useArchive"
                            v-model="showArchived"
                            :label="$t('labels.archived')"
                            icon="hi-archive-box-icon"
                            @change="refresh()" />

                        <div
                            v-if="update"
                            class="hts-switch"
                            @click="refresh()">
                            <div class="cursor-pointer">
                                <div class="flex items-center">
                                    <hi-arrow-path-icon class="icon w-5 h-5 mr-1" />
                                    <span>
                                        {{ $t('labels.update') }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="hts-data-table-pagination">
                        <span class="hts-data-table-pagination-summary-panel">
                            <span>
                                {{ $t('labels.total') }}
                            </span>
                            <span class="hts-data-table-pagination-number">
                                {{ totalItem }} {{ $t('labels.items') }}
                            </span>
                        </span>
                        <hts-popover
                            v-model="selectPageSize"
                            trigger="click"
                            class="hts-data-table-pagination-select-page-size">
                            <template #reference>
                                <span class="hts-data-table-pagination-number">
                                    {{ pageSize }}
                                </span>
                                <span>
                                    {{ $t('labels.items') }}/{{ $t('labels.page') }}
                                </span>
                            </template>
                            <ul class="hts-data-table-pagination-page-size-list">
                                <li
                                    v-for="(page, index) in pageSizes"
                                    :key="index"
                                    class="hts-data-table-pagination-page-size-item"
                                    @click="setPageSize(page)">
                                    {{ page }} <span>
                                        {{ $t('labels.items') }}/{{ $t('labels.page') }}
                                    </span>
                                </li>
                            </ul>
                        </hts-popover>
                        <span class="hts-data-table-pagination-summary-panel">
                            <div
                                class="hts-data-table-pagination-button"
                                :class="{ 'hts-disabled': currentPage <= 1 }"
                                aria-label="First Page"
                                tabindex="0">
                                <hi-chevron-double-left-icon
                                    class="w-7 h-7"
                                    @click="onBtFirst" />
                            </div>
                            <div
                                class="hts-data-table-pagination-button"
                                :class="{ 'hts-disabled': currentPage <= 1 }"
                                aria-label="Previous Page"
                                tabindex="0">
                                <hi-chevron-left-icon
                                    class="w-7 h-7"
                                    @click="onBtPrevious" />
                            </div>
                            <hts-popover
                                v-model="selectPage"
                                trigger="click"
                                class="hts-data-table-pagination-select-page">
                                <template #reference>
                                    <span class="hts-data-table-pagination-description">
                                        <span>
                                            {{ $t('labels.page') }}
                                        </span>
                                        <span class="hts-data-table-pagination-number">
                                            {{ currentPage }}
                                        </span>
                                        <span>
                                            /
                                        </span>
                                        <span class="hts-data-table-pagination-number">
                                            {{ totalPage }}
                                        </span>
                                    </span>
                                </template>
                                <input
                                    v-model="changePage"
                                    type="number"
                                    class="hts-data-table-pagination-select-page-input"
                                    :min="1"
                                    :max="totalPage"
                                    @keydown="onInputChangePage" >
                            </hts-popover>
                            <div
                                class="hts-data-table-pagination-button"
                                :class="{ 'hts-disabled': currentPage >= totalPage }"
                                aria-label="Next Page"
                                aria-disabled="true">
                                <hi-chevron-right-icon
                                    class="w-7 h-7"
                                    @click="onBtNext" />
                            </div>
                            <div
                                class="hts-data-table-pagination-button"
                                :class="{ 'hts-disabled': currentPage >= totalPage }"
                                aria-label="Last Page"
                                aria-disabled="true">
                                <hi-chevron-double-right-icon
                                    class="w-7 h-7"
                                    @click="onBtLast" />
                            </div>
                        </span>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ModuleRegistry } from '@ag-grid-community/core'
import { AgGridVue } from '@ag-grid-community/vue3'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import { RichSelectModule } from '@ag-grid-enterprise/rich-select'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import filterParams from './filter-option'
import AG_GRID_LOCALE_CUSTOM from './locale.custom'
import Selection from './selection'
import SwitchButton from './switch'

import FilterStatus from '@/components/data-table/-components/filter/status.vue'
const route = useRoute()
const ids = computed(() => {
  const value = String(route.query.ids) || []
  if (value === 'undefined') {
    return ''
  }
  return value
})

const components = {
  TableFilterStatus: FilterStatus
}

ModuleRegistry.registerModules([ServerSideRowModelModule, ClientSideRowModelModule, RowGroupingModule, MasterDetailModule, RichSelectModule])

const {
  selectedItems,
  exceptItems,
  selectionLoader,
  onSelectionChanged,
  checkEndSetCheckedAllByPage,
  onSelectionAll,
  selectionAll,
  toggleSelectedAll,
  checkItems,
  clearAllSelection,
  dataSelection
} = Selection()
const initialized = ref(false)
const loaded = ref(false)
const props = defineProps({
  server: {
    type: Object
  },
  autoScale: {
    type: Boolean,
    default: true
  },
  tools: {
    type: Array
  },
  headers: {
    type: Array,
    default: () => []
  },
  options: {
    type: Object,
    default: () => ({
      flex: 1,
      sortable: false,
      resizable: true,
      minWidth: 200,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      suppressHeaderMenuButton: false,
      suppressHeaderContextMenu: false
      // floatingFilter: true
      // filter: true
    })
  },
  masterDetail: {
    type: Boolean,
    default: true
  },
  detailRowAutoHeight: {
    type: Boolean,
    default: true
  },
  isSuppressRowTransform: {
    type: Boolean,
    default: true
  },
  isRowMaster: {
    type: Object,
    default: (dataItem) => {}
  },
  detailCellRendererParams: {
    type: Object,
    default: () => ({
      detailGridOptions: {
        columnDefs: []
      },
      getDetailRowData: (params) => {}
    })
  },
  detailCellRenderer: {
    type: Object,
    default: (params) => {}
  },
  detailRowHeight: {
    type: Number,
    default: 0
  },
  getContextMenuItems: {
    type: [Object, Function],
    default: (params) => {}
  },
  serverSideSortOnServer: {
    type: Boolean,
    default: true
  },
  serverSideFilterOnServer: {
    type: Boolean,
    default: true
  },
  items: {
    type: Array,
    default: () => []
  },
  animateRows: {
    type: Boolean,
    default: true
  },
  pagination: {
    type: Boolean,
    default: true
  },
  suppressRowClickSelection: {
    type: Boolean,
    default: true
  },
  paginationPageSize: {
    type: Number,
    default: 10
  },
  rowSelection: {
    type: String,
    default: 'multiple'
  },
  sideBar: {
    type: Object,
    default: () => ({
      toolPanels: []
    })
  },
  autoGroupColumnDef: {
    type: Object,
    default: () => ({
      minWidth: {
        type: Number,
        default: 200
      }
    })
  },
  alwaysShowHorizontalScroll: {
    type: Boolean,
    default: false
  },
  alwaysShowVerticalScroll: {
    type: Boolean,
    default: false
  },
  useSelection: {
    type: Boolean,
    default: false
  },
  autoSize: {
    type: Object,
    default: () => ({
      active: true,
      skipHeader: false
    })
  },
  queryExtend: Object,
  storeName: {
    type: String,
    default: ''
  },
  storeKey: {
    type: String,
    default: ''
  },
  viewDeleted: {
    type: Boolean,
    default: false
  },
  singleClickEdit: {
    type: Boolean,
    default: false
  },
  useArchive: {
    type: Object,
    default: () => {}
  },
  useRestore: {
    type: Object,
    default: () => {}
  },
  useDelete: Boolean,
  showDeletedEnable: Boolean,
  prefixModule: {
    type: String,
    default: ''
  },
  selectionActions: {
    type: Array,
    default: () => []
  },
  deleteFunction: Function,
  firstDataRerender: {
    type: Function
  },
  isCellClicked: {
    type: Boolean,
    default: true
  },
  isFooter: {
    type: Boolean,
    default: true
  },
  isRowSelectable: {
    type: [Function, null],
    default: null
  },
  isSelectAllTable: {
    type: Boolean,
    default: true
  },
  customStyle: {
    type: [String, Object]
  },
  enableCellChangeFlash: Boolean,
  uriParams: Object,
  update: {
    type: Boolean,
    default: true
  }
})
const emits = defineEmits(['selection-changed', 'row-click'])
const icons = {
  menu: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
  <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
</svg>
`
}

const {
  server,
  headers,
  options,
  items,
  animateRows,
  pagination,
  paginationPageSize,
  rowSelection,
  sideBar,
  autoScale,
  autoSize,
  storeName,
  storeKey,
  queryExtend,
  viewDeleted,
  prefixModule,
  useArchive,
  useRestore,
  useSelection,
  selectionActions,
  deleteFunction,
  firstDataRerender,
  singleClickEdit,
  isCellClicked,
  isSelectAllTable,
  customStyle,
  uriParams
} = toRefs(props)
const store = $store()
const methods = ['get', 'post', 'put', 'patch', 'delete']
const isServerValid = computed(
  () => (server && server.value && server.value.url && server.value.method && methods.includes(server.value.method.toLowerCase())) || false
)
const isServerSide = computed(() => isServerValid.value)
const actionTable = ref(null)
const filterApplied = ref(false)
watch(
  () => server.value,
  () => {
    initialized.value = false
    nextTick(() => {
      initialized.value = true
    })
  }
)
const gridApi = ref(null)
const columnApi = ref(null)
const root = ref()
const columnState = ref()
const deleted = ref(false)
const localeText = ref(null)
const request = ref({})
const defaultHeight = ref(500)
const width = ref(0)

const cHeaders = computed(() => {
  // Sao chép headers để tránh thay đổi trực tiếp
  let updatedHeaders = [...headers.value]

  // Thêm cột 'selection' nếu cần
  const findCheckBox = updatedHeaders.findIndex((item) => item.key === 'selection')
  if (findCheckBox === -1 && useSelection.value) {
    updatedHeaders.unshift({
      key: 'selection',
      sortable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMenu: true,
      width: 50,
      maxWidth: 50,
      headerCheckboxSelection: isSelectAllTable.value,
      checkboxSelection: true,
      headerCheckboxSelectionCurrentPageOnly: true
    })
  }

  // Áp dụng các thay đổi cho từng phần tử
  updatedHeaders = updatedHeaders.map((item) => {
    // Thêm filter nếu có
    if (item.filter && item.filter in filterParams) {
      Object.assign(item, filterParams[item.filter])
    }

    // Thêm floating filter nếu cần
    // if (Object.keys(components).includes(item.filter)) {
    //   if (!item.floatingFilterComponent) {
    //     item.floatingFilterComponent = item.filter
    //     item.floatingFilterComponentParams = item.filterParams
    //     // item.floatingFilter = true
    //   }
    // }

    if (item.floatingFilterComponent) {
      item.floatingFilter = true
    }

    // Thiết lập kích thước
    if (item.key !== 'selection') {
      const fieldActions = ['action']
      if (fieldActions.includes(item.field)) {
        if (!item.maxWidth && !item.minWidth) {
          item.minWidth = 100
          item.maxWidth = 100
        }
      }

      if (!item.minWidth) {
        item.minWidth = 180
      }
      if (item.maxWidth && item.maxWidth < item.minWidth) {
        item.maxWidth = item.minWidth
      }
      if (item.width && item.width < item.minWidth) {
        item.width = item.minWidth
      }
    }

    // Xử lý pinned theo kích thước màn hình
    if (width.value < 1024 && width.value) {
      delete item.pinned
      delete item.lockPosition
      delete item.lockPinned
    } else if (item.field === 'action') {
      item.pinned = 'right'
      item.lockPosition = true
      item.lockPinned = true
    }

    return item
  })

  // Lọc bỏ các item không cần thiết
  updatedHeaders = updatedHeaders.filter((item) => {
    const fieldActions = ['action']

    if (fieldActions.includes(item.field) && typeof item?.cellRendererParams !== 'function') {
      if (!item?.cellRendererParams?.items || !item.cellRendererParams.items.length) {
        return false
      }
    }
    return true
  })

  // Lọc theo trạng thái showArchived và showDeleted
  if (showArchived.value || showDeleted.value) {
    return updatedHeaders.filter((item) => !item.showActiveOnly)
  }

  return updatedHeaders
})

const cPaginationPageSize = ref(10)
const datasource = {
  getRows: async (params) => {
    if (!isServerValid.value) {
      params.success({
        rowData: items.value || [],
        rowCount: items.value.length || 0
      })
      return
    }
    try {
      request.value = {
        ...(params.request || {}),
        ...(queryExtend.value || {}),
        ...{
          onlyArchived: showArchived.value,
          onlyDeleted: showDeleted.value,
          ids: ids.value || []
        }
      }

      const response = await $api(server.value, { uriParams: uriParams.value, body: request.value })
      const responseData = response.data || {}
      const { data, success } = responseData.value || {}
      if (success) {
        params.success({
          rowData: data.rows.oldData ? data.rows.oldData : data.rows ? data.rows : [],
          rowCount: data.count || 0
        })
      } else {
        params.success({
          rowData: [],
          rowCount: 0
        })
      }
    } catch (e) {
      params.success({
        rowData: [],
        rowCount: 0
      })
    }
  }
}

const onGridReady = (params) => {
  gridApi.value = params.api
  columnApi.value = params.columnApi
  selectionLoader(params.api, emits)

  if (columnState.value?.length) {
    columnApi.value.applyColumnState({
      state: columnState.value,
      applyOrder: true
    })
  }
  nextTick(() => {
    loaded.value = true
    init(params)
  })
  if (typeof firstDataRerender.value === 'function') {
    firstDataRerender.value(params)
  }

  gridApi.value.addEventListener('storeRefreshed', () => {
    // Chạy các hàm sau khi dữ liệu server-side đã được làm mới
    gridApi.value.refreshCells({ force: true })
    gridApi.value.redrawRows()

    const page = currentPage.value - 1
    if (page >= 0) {
      gridApi.value.paginationGoToPage(page)
    }
  })
}

const init = (params) => {
  if (isServerSide.value) {
    params.api.setServerSideDatasource(datasource)
  }
}

/** Table events */
const onDragStopped = (params) => {
  if (storeName.value && storeKey.value) {
    const columnState = params.columnApi.getColumnState()
    nextTick(() => {
      if (typeof store[storeName.value][`setColumn${storeKey.value}State`] === 'function') {
        store[storeName.value][`setColumn${storeKey.value}State`](columnState)
      }
    })
  }
}
const onSortChanged = (params) => {
  if (storeName.value && storeKey.value) {
    const columnState = params.columnApi.getColumnState()
    nextTick(() => {
      if (typeof store[storeName.value][`setColumn${storeKey.value}State`] === 'function') {
        store[storeName.value][`setColumn${storeKey.value}State`](columnState)
      }
    })
  }
}
const autoSizeAll = () => {
  const allColumnIds = []
  const exceptColumn = ['selection']
  if (columnApi && columnApi.value) {
    columnApi.value.getColumns().forEach((column) => {
      const columnInfo = column.getUserProvidedColDef() ?? {}
      if (columnInfo && !exceptColumn.includes(columnInfo.key)) {
        allColumnIds.push(column.getId())
      }
    })
    columnApi.value.autoSizeColumns(allColumnIds, autoSize.value.skipHeader)
  }
}
const onFirstDataRendered = () => {
  if (autoSize.value.active) {
    setTimeout(() => {
      if (!columnState.value?.length) {
        // autoSizeAll()
      }
    }, 300)
  }
}

const onResized = () => {
  setTimeout(() => {
    width.value = window.innerWidth

    if (!autoScale.value) {
      root.value.style.height = defaultHeight.value + 'px'
      return
    }

    if (!root.value) {
      return
    }

    const rect = root.value.getBoundingClientRect()
    const top = rect.top
    const clientHeight = window.innerHeight
    let height = clientHeight - top - 20
    if (height < 100) {
      height = defaultHeight.value
    }
    root.value.style.height = height + 'px'
  }, 300)
}

const refresh = (route = [], purge = false) => {
  selectionAll.value = false
  selectedItems.value = []

  if (isServerValid.value) {
    gridApi.value.refreshServerSide({
      route,
      purge
    })
  }
}

/** Selection */
const totalSelectedItem = computed(() => {
  if (selectionAll.value) {
    return totalItem.value - exceptItems.value.length
  }
  return selectedItems.value.length
})
const hasSelectedItem = computed(() => {
  return selectedItems.value.length || checkItems.value.selectAll
})
const selectAllVisible = computed(() => {
  return useSelection.value && hasSelectedItem.value && totalItem.value > pageSize.value && totalSelectedItem.value < totalItem.value && isSelectAllTable.value
})
const checkActionVisible = (action) => {
  if (action.isArchive) {
    return showArchived.value
  }
  if (action.single && (selectedItems.value.length > 1 || checkItems.value.selectAll)) {
    return false
  }
  if (action.multiple && selectedItems.value.length < 2 && !checkItems.value.selectAll) {
    return false
  }
  if (action.visible === undefined) {
    return true
  }
  if (typeof action.visible === 'function') {
    return action.visible(dataSelection())
  }
  return true
}
const handleClickSelectionAction = (action) => {
  if (typeof action.fn === 'function') {
    if (action.single && selectedItems.value.length === 1 && !checkItems.value.selectAll) {
      action.fn(selectedItems.value[0])
    } else {
      action.fn({ ...dataSelection(), ...request.value })
    }
  }
}

const cStyle = computed(() => {
  return Object.assign({ 'min-height': '400px', height: 'calc(100% - 54px)' }, customStyle.value)
})
/** Archive & restore */
const archiveVisible = ref(false)
const restoreVisible = ref(false)
const isDeleted = ref(false)
const isArchived = ref(false)
const showDeleted = computed({
  get () {
    return isDeleted.value
  },
  set (val) {
    if (val) {
      isArchived.value = false
    }
    isDeleted.value = val
  }
})
const showArchived = computed({
  get () {
    if (storeName.value && storeKey.value) {
      return store[storeName.value][`getIs${storeKey.value}Archive`] ?? false
    }
    return false
  },
  set (val) {
    if (val) {
      isDeleted.value = false
    }
    store[storeName.value][`setIs${storeKey.value}Archive`](val)
  }
})

const openArchive = () => {
  archiveVisible.value = true
}
const hideArchive = () => {
  archiveVisible.value = false
}
const confirmArchive = async () => {
  await $api(useArchive.value.api, {
    body: { ...dataSelection(), ...request.value }
  }).then((data) => {
    const resData = data?.data?.value
    if (resData?.success) {
      process.client && $toast().success('Lưu trữ dữ liệu thành công')
      archiveVisible.value = false
      refresh()
    }
  })
}
const openRestore = () => {
  restoreVisible.value = true
}
const hideRestore = () => {
  restoreVisible.value = false
}
const confirmRestore = async () => {
  await $api(useRestore.value.api, {
    body: { ...dataSelection(), ...request.value }
  }).then((data) => {
    const resData = data?.data?.value
    if (resData?.success) {
      process.client && $toast().success('Khôi phục dữ liệu thành công')
      restoreVisible.value = false
      refresh()
    }
  })
}
/** Pagination */
const pageSizes = ref([10, 20, 50, 100])
const selectPage = ref(false)
const selectPageSize = ref(false)
const lastPageFound = ref(false)
const pageSize = ref(0)
const currentPage = ref(1)
const changePage = ref(1)
watch(
  () => currentPage.value,
  (val) => {
    changePage.value = val
  }
)
watch(
  () => selectPage.value,
  (val) => {
    if (!val) {
      setPage(changePage.value)
    }
  }
)
const onInputChangePage = (event) => {
  if (event.key === 'Enter') {
    selectPage.value = false
  }
}
const totalPage = ref(1)
const totalItem = ref(1)
const setPage = (page) => {
  page = parseInt(page)
  if (page > totalPage.value) {
    page = changePage.value = totalPage.value
  }
  if (page < 1) {
    page = changePage.value = 1
  }
  gridApi.value.paginationGoToPage(page - 1)
}
const setPageSize = (pageSize) => {
  cPaginationPageSize.value = pageSize
  gridApi.value.paginationSetPageSize(pageSize)
}
const onPaginationChanged = () => {
  selectionLoader(gridApi.value, emits)
  if (gridApi.value) {
    lastPageFound.value = gridApi.value.paginationIsLastPageFound()
    pageSize.value = gridApi.value.paginationGetPageSize()
    currentPage.value = gridApi.value.paginationGetCurrentPage() + 1
    totalPage.value = gridApi.value.paginationGetTotalPages()
    totalItem.value = gridApi.value.paginationGetRowCount()
  }
  checkEndSetCheckedAllByPage()
}

const onBtFirst = () => {
  if (currentPage.value <= 1) {
    return
  }
  gridApi.value.paginationGoToFirstPage()
}
const onBtLast = () => {
  if (currentPage.value >= totalPage.value) {
    return
  }
  gridApi.value.paginationGoToLastPage()
}
const onBtNext = () => {
  if (currentPage.value >= totalPage.value) {
    return
  }
  gridApi.value.paginationGoToNextPage()
}
const onBtPrevious = () => {
  if (currentPage.value <= 1) {
    return
  }
  gridApi.value.paginationGoToPreviousPage()
}

const setQuickFilter = (val) => {
  if (!gridApi.value) {
    return
  }

  gridApi.value.setQuickFilter(val)
}

const setFilterModel = $lodash.debounce((val) => {
  const model = gridApi.value.getFilterModel()

  Object.keys(val).forEach((item) => {
    model[item] = val[item]
  })

  gridApi.value.setFilterModel(model)
  refresh()
}, 300)

/** Delete & archive function */
const handleDeleteSelectedItems = () => {
  if (typeof deleteFunction.value === 'function') {
    deleteFunction.value({
      selectionAll: selectionAll.value,
      except: exceptItems.value,
      selectedItems: selectedItems.value
    })
  }
}

/** Update data */
const setItem = (id, data) => {
  if (!id || !data) {
    return
  }
  const rowNode = gridApi.value.getRowNode(id)
  if (!rowNode) {
    return
  }
  Object.keys(data).forEach((key) => {
    rowNode.setDataValue(key, data[key])
  })
}
/** Expose */
defineExpose({
  loaded,
  refresh,
  gridApi,
  columnApi,
  setQuickFilter,
  setFilterModel,
  setItem,
  onResized
})

onBeforeMount(() => {
  if (storeName.value && storeKey.value) {
    columnState.value = store[storeName.value][`getColumn${storeKey.value}State`]
  }
  localeText.value = AG_GRID_LOCALE_CUSTOM
})

onMounted(() => {
  cPaginationPageSize.value = paginationPageSize.value
  nextTick(() => {
    initialized.value = true
  })
  if (process.client) {
    window.addEventListener('resize', onResized)
    onResized()
  }
})
const handleChangeSelection = () => {
  gridApi.value.clearFocusedCell()
  if (!isServerSide.value) {
    return onSelectionClientSide()
  } else {
    return onSelectionChanged()
  }
}

const onSelectionClientSide = () => {
  const selectedRows = gridApi.value.getSelectedRows()
  emits('selectionChanged', selectedRows)
}

const onRowClicked = (event) => {
  if (isCellClicked.value) {
    event.api.forEachNode((node) => {
      node.isClick = false
    })
    event.node.isClick = true
    emits('row-click', event.node?.data)
  }
}

const getRowStyle = (params) => {
  if (params.node.isClick === true) {
    return { 'background-color': 'rgb(165 243 252)', 'z-index': 0 }
  }
}
const getRowId = (params) => {
  return params.data.id || params.data._id
}

// Xử lý đổi màu icon filter
const onFilterChanged = () => {
  const allFilters = gridApi.value.getFilterModel()

  Object.keys(allFilters).forEach((columnId) => {
    const iconElement = document.querySelector(`.ag-header-cell[col-id="${columnId}"] .ag-header-icon`)
    const isFilterApplied = allFilters[columnId] !== undefined

    if (iconElement) {
      if (isFilterApplied) {
        iconElement.classList.add('filter-applied')
      } else {
        iconElement.classList.remove('filter-applied')
      }
    }
  })
}

onBeforeUnmount(() => {
  gridApi.value?.removeEventListener('storeRefreshed')
  window?.removeEventListener('resize', onResized)
})
</script>
